import { Button, Empty, Tag, Typography } from "antd";
import moment from "moment";

const { Text } = Typography;

const ANNOTATION_TYPE_COLORS: Record<string, string> = {
    issue: "red",
    review: "gold",
    info: "blue",
};

const ANNOTATION_PRIORITY_COLORS: Record<string, string> = {
    high: "red",
    medium: "orange",
    low: "default",
};

const ANNOTATION_STATE_LABELS: Record<string, string> = {
    open: "Open",
    in_progress: "In progress",
    resolved: "Resolved",
    dismissed: "Dismissed",
};

export type RecordDiscussionEntry = IRecordCommentPreview | IRecordAnnotationPreview;

interface IRecordDiscussionPreviewProps {
    kind: "comments" | "annotations";
    entries: RecordDiscussionEntry[];
    total: number;
    hasMore?: boolean;
    onViewAll?: () => void;
    viewAllLabel?: string;
}

const isAnnotation = (entry: RecordDiscussionEntry): entry is IRecordAnnotationPreview => (entry as IRecordAnnotationPreview).annotationId !== undefined;

const formatTimestamp = (value?: string) => {
    if (!value) {
        return { relative: "", absolute: "" };
    }
    const parsed = moment(value);
    if (!parsed.isValid()) {
        return { relative: "", absolute: value };
    }
    return { relative: parsed.fromNow(), absolute: parsed.format("DD MMM YYYY, HH:mm") };
};


const RecordDiscussionPreview = ({ kind, entries, total, hasMore, onViewAll, viewAllLabel }: IRecordDiscussionPreviewProps) => {
    const noun = kind === "comments" ? "comment" : "annotation";
    const shown = entries || [];
    const remaining = Math.max(0, total - shown.length);

    return (
        <div className="w-[320px]">
            <div className="mb-2 flex items-baseline justify-between gap-2">
                <Text strong className="text-xs uppercase tracking-wide text-gray-500">
                    {kind === "comments" ? "Comments" : "Annotations"}
                </Text>
                <Text type="secondary" className="text-xs">
                    {total} {noun}
                    {total === 1 ? "" : "s"}
                    {hasMore ? "+" : ""}
                </Text>
            </div>

            {shown.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${noun}s on this record yet.`} className="my-2" />
            ) : (
                <ul className="m-0 max-h-[260px] list-none overflow-y-auto p-0">
                    {shown.map((entry, index) => {
                        const annotation = isAnnotation(entry) ? entry : null;
                        const key = annotation ? annotation.annotationId : (entry as IRecordCommentPreview).commentId || `entry-${index}`;
                        const { relative, absolute } = formatTimestamp(entry.createdDate);
                        const author = entry.createdByName || entry.createdBy || "Unknown user";

                        return (
                            <li key={key} className="border-b border-gray-100 py-2 last:border-b-0">
                                <div className="flex items-baseline justify-between gap-2">
                                    <Text strong className="truncate text-xs">
                                        {author}
                                    </Text>
                                    <Text type="secondary" className="shrink-0 text-[11px]" title={absolute}>
                                        {relative}
                                    </Text>
                                </div>

                                {annotation && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {annotation.type && (
                                            <Tag color={ANNOTATION_TYPE_COLORS[annotation.type] || "default"} className="m-0 text-[10px]">
                                                {annotation.type}
                                            </Tag>
                                        )}
                                        {annotation.state && (
                                            <Tag className="m-0 text-[10px]">{ANNOTATION_STATE_LABELS[annotation.state] || annotation.state}</Tag>
                                        )}
                                        {annotation.priority && (
                                            <Tag color={ANNOTATION_PRIORITY_COLORS[annotation.priority] || "default"} className="m-0 text-[10px]">
                                                {annotation.priority}
                                            </Tag>
                                        )}
                                        {annotation.closed && (
                                            <Tag color="green" className="m-0 text-[10px]">
                                                closed
                                            </Tag>
                                        )}
                                    </div>
                                )}

                                <Text className="mt-1 block whitespace-pre-wrap break-words text-xs text-gray-700">
                                    {entry.content || (annotation?.closeComment ?? "")}
                                </Text>

                                {annotation?.targetName && (
                                    <Text type="secondary" className="mt-0.5 block text-[11px]">
                                        on {annotation.targetName}
                                    </Text>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {!!onViewAll && (
                <div className="mt-2 flex justify-end border-t border-gray-100 pt-2">
                    <Button type="link" size="small" className="h-auto p-0 text-xs" onClick={onViewAll}>
                        {viewAllLabel || (remaining > 0 || hasMore ? `View all ${total}${hasMore ? "+" : ""} →` : `Open ${noun}s →`)}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default RecordDiscussionPreview;
